import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { ensureListing } from '../../util/data';
import { cartLineItems as apiCartLineItems } from '../../util/api';
import { formatMoney } from '../../util/currency';
import { createSlug } from '../../util/urlHelpers';
import { createResourceLocatorString, findRouteByRouteName } from '../../util/routes';
import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import {
  buildProviderFrancoData,
  buildProviderGroupsFromCart,
  calculateProviderShippingCost,
  resolveShippingMethodIdForItem,
} from '../../util/cartProviderGroups';

const { Money } = sdkTypes;

import {
  H1,
  H3,
  H4,
  LayoutSingleColumn,
  ListingCard,
  NamedLink,
  Page,
  PrimaryButton,
  ResponsiveImage,
  TrustBanner,
  IconDelete,
  IconSpinner
} from '../../components';
import SkeepersCarouselReviews from '../../components/SkeepersCarouselReviews/SkeepersCarouselReviews';
import ProviderShippingSelectors from '../../components/ProviderShippingSelectors/ProviderShippingSelectors';
import IconCard from '../../components/SavedCardDetails/IconCard/IconCard';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';

import {
  loadCart,
  updateCartItemQuantity,
  removeFromCart,
  updateProviderShippingMethod,
  cartLoadSuccess,
  setCartPromoCode,
  addCartPromoCode,
  removeCartPromoCode,
  setCartPromoCodes,
} from '../../ducks/cart.duck';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import MultiVendorCheckoutModal from './MultiVendorCheckoutModal';
import PrintempsRoboupCartModal from '../../components/PrintempsRoboupPromo/PrintempsRoboupCartModal';
import {
  LINE_ITEM_PRINTEMPS_ROBOUP_DISCOUNT,
  LINE_ITEM_REOLINK_WEEKEND_2026_DISCOUNT,
  LINE_ITEM_T600MAI50_DISCOUNT,
} from '../../util/types';
import { getRecentlyViewedListings, toListingCardListing } from '../../util/recentlyViewedListings';
import css from './CartPage.module.css';

const StripeLogo = () => (
  <svg
    className={css.stripeLogo}
    viewBox="0 0 48 20"
    role="img"
    aria-label="Stripe"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect x="0" y="0" width="48" height="20" rx="6" fill="#635BFF" />
    <text x="24" y="14" textAnchor="middle" fill="#FFFFFF" fontSize="11" fontWeight="700">
      Stripe
    </text>
  </svg>
);

const CartItem = ({ item, listing, providerId, onUpdateQuantity, onRemove, config, hasFreeShipping }) => {
  const intl = useIntl();
  const [isUpdating, setIsUpdating] = useState(false);

  const { listingId, quantity, deliveryMethod } = item;
  const price = listing?.attributes?.price;
  const title = listing?.attributes?.title || 'Product';
  const images = listing?.images || [];
  const firstImage = images.length > 0 ? images[0] : null;
  const publicData = listing?.attributes?.publicData || {};

  // Validate that quantity and price are valid numbers
  const isValidQuantity = typeof quantity === 'number' && !isNaN(quantity) && quantity > 0;
  const isValidPrice = price &&
    typeof price.amount === 'number' &&
    !isNaN(price.amount) &&
    price.amount > 0 &&
    price.currency;

  // Shipping info is now managed at provider level, not item level

  // Early return if essential data is invalid
  if (!isValidQuantity) {
    return null;
  }

  const handleQuantityChange = async (newQuantity) => {
    if (newQuantity === quantity) return;

    setIsUpdating(true);
    try {
      await onUpdateQuantity({ listingId, providerId, quantity: newQuantity });
    } catch (error) {
      // Silently handle error
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRemove = async () => {
    setIsUpdating(true);
    try {
      await onRemove({ listingId, providerId });
    } catch (error) {
      // Silently handle error
    } finally {
      setIsUpdating(false);
    }
  };

  const itemTotal = isValidPrice && price.currency ? (() => {
    try {
      const totalAmount = price.amount * quantity;
      // Double-check that the calculation result is valid and currency is valid
      if (typeof totalAmount === 'number' && !isNaN(totalAmount) && totalAmount > 0 && 
          price.currency && typeof price.currency === 'string') {
        return formatMoney(intl, new Money(totalAmount, price.currency));
      }
      return null;
    } catch (error) {
      return null;
    }
  })() : null;

  return (
    <div className={css.cartItem}>
      <div className={css.itemImage}>
        {firstImage ? (
          <ResponsiveImage
            rootClassName={css.productImage}
            alt={title}
            image={firstImage}
            variants={['listing-card', 'listing-card-2x']}
          />
        ) : (
          <div className={css.noImage}>
            <FormattedMessage id="CartPage.noImage" />
          </div>
        )}
      </div>

      <div className={css.itemDetails}>
        <div className={css.itemInfo}>
          <H4 className={css.itemTitle}>
            <NamedLink
              name="ListingPage"
              params={{ id: listingId, slug: createSlug(title) }}
              className={css.titleLink}
            >
              {title}
            </NamedLink>
          </H4>

          {itemTotal && (
            <div className={css.itemTotal}>
              {itemTotal}
            </div>
          )}
        </div>

        <div className={css.priceInfo}>
          {isValidPrice && price.currency && (
            <div className={css.itemPrice}>
              <span className={css.unitPriceLabel}>
                <FormattedMessage id="CartPage.unitPrice" defaultMessage="Unit price" />
              </span>
              <span className={css.unitPriceValue}>{formatMoney(intl, price)}</span>
            </div>
          )}
        </div>

        <div className={css.itemControls}>
          <div className={css.quantityControls}>
            <label className={css.quantityLabel}>
              <FormattedMessage id="CartPage.quantity" />
            </label>
            <div className={css.quantitySelector}>
              <button
                className={css.quantityBtn}
                onClick={() => handleQuantityChange(quantity - 1)}
                disabled={isUpdating || !isValidQuantity || quantity <= 1}
                aria-label={intl.formatMessage({ id: 'CartPage.decreaseQuantity' })}
              >
                −
              </button>
              <span className={css.quantityValue}>{isValidQuantity ? quantity : '—'}</span>
              <button
                className={css.quantityBtn}
                onClick={() => handleQuantityChange(quantity + 1)}
                disabled={isUpdating || !isValidQuantity}
                aria-label={intl.formatMessage({ id: 'CartPage.increaseQuantity' })}
              >
                +
              </button>
            </div>
          </div>

          <button
            className={css.removeBtn}
            onClick={handleRemove}
            disabled={isUpdating}
            aria-label={intl.formatMessage({ id: 'CartPage.removeItem' })}
          >
            <IconDelete className={css.removeIcon} />
            <FormattedMessage id="CartPage.remove" />
          </button>
        </div>

        {/* Shipping method is now managed at provider group level */}
      </div>
    </div>
  );
};

const CartPageComponent = props => {
  const config = useConfiguration();
  const intl = useIntl();
  const routes = useRouteConfiguration();
  const history = useHistory();
  const {
    cart,
    cartPromoCode,
    cartPromoCodes = [],
    cartBundlePromoCodes = [],
    currentUser,
    loadCart,
    listingsById,
    updateCartItemQuantity,
    removeFromCart,
    updateProviderShippingMethod,
    callSetInitialValues,
    onInitializeCardPaymentData,
    clearCart,
    onSetCartPromoCode,
    onAddCartPromoCode,
    onRemoveCartPromoCode,
    onSetCartPromoCodes,
  } = props;

  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [promoDraft, setPromoDraft] = useState('');
  const [promoError, setPromoError] = useState(null);
  const [expectedListingCount, setExpectedListingCount] = useState(0);
  const [isMultiVendorModalOpen, setIsMultiVendorModalOpen] = useState(false);
  const [pendingCheckoutGroups, setPendingCheckoutGroups] = useState(null);
  const [pendingGuestCheckout, setPendingGuestCheckout] = useState(false);
  const [invalidPromoCodes, setInvalidPromoCodes] = useState([]);
  const [recentlyViewedItems, setRecentlyViewedItems] = useState([]);
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 767px)').matches : false
  );

  const cartPromoCodesKey = (cartPromoCodes || []).join('|');

  useEffect(() => {
    const refreshRecentlyViewed = () => {
      setRecentlyViewedItems(getRecentlyViewedListings());
    };

    refreshRecentlyViewed();
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', refreshRecentlyViewed);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('storage', refreshRecentlyViewed);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const handleViewportChange = event => {
      setIsMobileViewport(event.matches);
    };
    setIsMobileViewport(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleViewportChange);
      return () => mediaQuery.removeEventListener('change', handleViewportChange);
    }

    mediaQuery.addListener(handleViewportChange);
    return () => mediaQuery.removeListener(handleViewportChange);
  }, []);

  useEffect(() => {
    // Load cart then fetch any missing listings to show titles
    loadCart().then(cartObj => {
      // Debug log cart content
      if (typeof window !== 'undefined') {
      }
      const allRefs = Object.values(cartObj || {})
        .flatMap(providerData => {
          // Handle both new format { items } and legacy format (array)
          const items = Array.isArray(providerData) ? providerData : (providerData.items || []);
          return items;
        })
        .map(it => ({ id: { uuid: it.listingId }, type: 'listing' }));

      // Track how many listings we expect to load
      const uniqueListingIds = new Set(allRefs.map(ref => ref.id.uuid).filter(Boolean));
      setExpectedListingCount(uniqueListingIds.size);

      if (allRefs.length > 0) {
        // Always clear the loader once the fetch settles, even if some cart
        // listings fail to load (e.g. a stale id lingering in localStorage).
        // Otherwise the spinner would spin forever waiting for a listing that
        // never resolves.
        Promise.resolve(props.fetchMissingListings(allRefs)).finally(() => {
          setIsInitialLoading(false);
        });
      } else {
        // No listings to load
        setIsInitialLoading(false);
      }
    }).catch(() => {
      setIsInitialLoading(false);
    });
  }, []);

  // Monitor when all expected listings are loaded
  useEffect(() => {
    const loadedCount = Object.keys(listingsById).length;
    // When we have loaded all expected listings, stop showing loader
    if (expectedListingCount > 0 && loadedCount >= expectedListingCount) {
      setIsInitialLoading(false);
    }
  }, [listingsById, expectedListingCount]);

  const providerGroups = buildProviderGroupsFromCart(cart, listingsById);
  const requiredRecentCount = isMobileViewport ? 2 : 5;
  const recentListingCards = useMemo(
    () =>
      recentlyViewedItems
        .map(item => toListingCardListing(item, Money))
        .filter(listing => listing && listing?.id?.uuid),
    [recentlyViewedItems]
  );
  const shouldShowRecentRow = recentListingCards.length >= requiredRecentCount;
  const recentListingCardsToRender = shouldShowRecentRow
    ? recentListingCards.slice(0, requiredRecentCount)
    : [];

  // Local naive totals (fallback)
  const grandTotal = providerGroups.reduce((total, group) => {
    const groupTotal = group.items.reduce((groupSum, item) => {
      const listing = listingsById[item.listingId?.uuid || item.listingId];
      const price = listing?.attributes?.price;
      const itemQuantity = item.quantity;

      // Validate that both price and quantity are valid numbers
      const isValidPrice = price && typeof price.amount === 'number' && !isNaN(price.amount);
      const isValidQuantity = typeof itemQuantity === 'number' && !isNaN(itemQuantity) && itemQuantity > 0;

      return groupSum + (isValidPrice && isValidQuantity ? price.amount * itemQuantity : 0);
    }, 0);
    return total + groupTotal;
  }, 0);

  const totalItems = providerGroups.reduce((total, group) => {
    return total + group.items.reduce((sum, item) => {
      const itemQuantity = item.quantity;
      const isValidQuantity = typeof itemQuantity === 'number' && !isNaN(itemQuantity) && itemQuantity > 0;
      return sum + (isValidQuantity ? itemQuantity : 0);
    }, 0);
  }, 0);

  const currency = providerGroups[0]?.items[0] ?
    listingsById[providerGroups[0].items[0].listingId?.uuid || providerGroups[0].items[0].listingId]?.attributes?.price?.currency :
    config.currency;

  // Validate that currency is a valid string
  const isValidCurrency = typeof currency === 'string' && currency.length > 0;

  const providerFrancoData = buildProviderFrancoData(providerGroups, listingsById);

  // Check if any provider has free shipping
  // Estimated line items and totals fetched from backend (with shipping)
  const [estimated, setEstimated] = useState({
    lines: [],
    subtotal: 0,
    discount: 0,
    shipping: 0,
    total: 0,
  });

  useEffect(() => {
    const items = providerGroups.flatMap(group =>
      group.items.map(it => {
        const listingId = it.listingId?.uuid || it.listingId;
        const shippingMethodId = resolveShippingMethodIdForItem(group, it);

        return {
          listingId,
          isOwnListing: false,
          orderData: {
            stockReservationQuantity: it.quantity,
            ...(it.deliveryMethod ? { deliveryMethod: it.deliveryMethod } : {}),
            ...(shippingMethodId ? { shippingMethodId } : {}),
          },
        };
      })
    );

    if (items.length === 0) {
      setEstimated({ lines: [], subtotal: 0, discount: 0, shipping: 0, total: 0 });
      return;
    }

    const promoCodesForRequest =
      cartPromoCodes && cartPromoCodes.length > 0
        ? cartPromoCodes
        : cartPromoCode
        ? [cartPromoCode]
        : [];

    apiCartLineItems(items, promoCodesForRequest)
      .then(({ data }) => {
        const lines = Array.isArray(data) ? data : [];
        const sum = (filterFn) => lines
          .filter(li => {
            try { return filterFn(li); } catch { return false; }
          })
          .reduce((acc, li) => acc + (li?.lineTotal?.amount || 0), 0);

        const subtotal = sum(li => String(li.code || '').indexOf('line-item/item') === 0);
        const discount = sum(
          li =>
            li.code === LINE_ITEM_PRINTEMPS_ROBOUP_DISCOUNT ||
            li.code === LINE_ITEM_REOLINK_WEEKEND_2026_DISCOUNT ||
            li.code === LINE_ITEM_T600MAI50_DISCOUNT ||
            li.code === 'line-item/loyalty-discount' ||
            (typeof li.code === 'string' && li.code.startsWith('line-item/bundle-discount-'))
        );

        // Per-code validation: flag codes that did not produce any discount
        // line item (including hardcoded promos and bundle promos).
        if (promoCodesForRequest.length > 0) {
          const hardcodedDiscountLines = lines.filter(
            li =>
              li.code === LINE_ITEM_PRINTEMPS_ROBOUP_DISCOUNT ||
              li.code === LINE_ITEM_REOLINK_WEEKEND_2026_DISCOUNT ||
              li.code === LINE_ITEM_T600MAI50_DISCOUNT
          );
          const bundleDiscountCodes = new Set(
            lines
              .filter(
                li => typeof li.code === 'string' && li.code.startsWith('line-item/bundle-discount-')
              )
              .map(li => (li.metadata?.promoCode || '').toUpperCase())
              .filter(Boolean)
          );
          const hasAnyHardcodedDiscount = hardcodedDiscountLines.length > 0;
          const hasLoyaltyDiscount = lines.some(li => li.code === 'line-item/loyalty-discount');

          const allBundleCodesUpper = new Set([
            ...Array.from(bundleDiscountCodes),
            ...(cartBundlePromoCodes || []).map(c => c.toUpperCase()),
          ]);
          const invalids = promoCodesForRequest.filter(code => {
            const upper = code.toUpperCase();
            if (allBundleCodesUpper.has(upper)) return false;
            // Loyalty codes (NEO-) are validated server-side
            if (upper.startsWith('NEO-') && hasLoyaltyDiscount) return false;
            // Hardcoded promo codes — kept permissive since they match exactly one
            // of the known codes and the backend rejects unknown codes silently.
            if (hasAnyHardcodedDiscount) return false;
            return true;
          });
          setInvalidPromoCodes(invalids);

          const nonBundleCodesForRequest = promoCodesForRequest.filter(
            code => !allBundleCodesUpper.has(code.toUpperCase())
          );
          if (invalids.length > 0 && nonBundleCodesForRequest.length === 1 && discount === 0) {
            setPromoError(intl.formatMessage({ id: 'CartPage.promoNotEligible' }));
          } else if (invalids.length === 0) {
            setPromoError(null);
          }
        } else {
          setInvalidPromoCodes([]);
          setPromoError(null);
        }

        const providerShippingTotal = providerGroups.reduce((sumShipping, group) => {
          const providerInfo = providerFrancoData.find(
            pf => pf.providerId === group.providerId
          );
          return sumShipping + calculateProviderShippingCost(group, providerInfo);
        }, 0);

        const shipping = providerShippingTotal;
        const finalTotal = subtotal + shipping + discount;

        setEstimated({ lines, subtotal, discount, shipping, total: finalTotal });
      })
      .catch((error) => {
        // If error is 404 (e.g., api/cart-line-items endpoint not found), flush localStorage and show empty cart
        const is404Error = 
          error?.status === 404 || 
          error?.statusCode === 404 || 
          (error?.message && (error.message.includes('404') || error.message.includes('cart-line-items'))) ||
          (error?.toString && error.toString().includes('404'));
        
        if (is404Error && clearCart) {
          clearCart();
        }
        setEstimated({ lines: [], subtotal: 0, discount: 0, shipping: 0, total: 0 });
      });
  }, [
    JSON.stringify(cart),
    cartPromoCode,
    cartPromoCodesKey,
    (cartBundlePromoCodes || []).join('|'),
    Object.keys(listingsById).length
  ]);

  const proceedToCheckout = (checkoutGroups, { isGuestCheckout = false } = {}) => {
    try {
      if (checkoutGroups.length === 0) return;

      // Take the first provider group for immediate checkout
      const firstGroup = checkoutGroups[0];
      
      // Store remaining provider groups in queue for sequential processing
      if (checkoutGroups.length > 1) {
        const remainingGroups = checkoutGroups.slice(1);
        if (typeof window !== 'undefined') {
          window.sessionStorage.setItem('bulkCheckoutQueue', JSON.stringify(remainingGroups));
        }
      } else {
        // Clear queue if only one provider
        if (typeof window !== 'undefined') {
          window.sessionStorage.removeItem('bulkCheckoutQueue');
        }
      }

      // Prepare initial values for CheckoutPage
      const initialValues = {
        listing: firstGroup.items[0].listing,
        orderData: firstGroup.items[0].orderData,
        confirmPaymentError: null,
        ...(cartPromoCode ? { promoCode: cartPromoCode } : {}),
        ...(cartPromoCodes && cartPromoCodes.length > 0 ? { promoCodes: cartPromoCodes } : {}),
        ...(typeof firstGroup.shippingPrice === 'number'
          ? { shippingPrice: firstGroup.shippingPrice }
          : {}),
        ...(firstGroup.shippingCurrency
          ? { shippingCurrency: firstGroup.shippingCurrency }
          : {}),
        // Add multi-item data if applicable
        ...(firstGroup.isMultiItem ? {
          multiItemPurchase: true,
          allItems: firstGroup.items,
        } : {}),
        ...(isGuestCheckout ? { isGuestCheckout: true } : {}),
      };

      const { setInitialValues } = findRouteByRouteName('CheckoutPage', routes);
      const saveToSessionStorage = !currentUser || isGuestCheckout;
      callSetInitialValues(setInitialValues, initialValues, saveToSessionStorage);
      onInitializeCardPaymentData();

      history.push(
        createResourceLocatorString(
          'CheckoutPage',
          routes,
          { id: firstGroup.items[0].listing.id.uuid, slug: createSlug(firstGroup.items[0].listing.attributes.title) },
          {}
        )
      );
    } catch (e) {
      // Silently handle error
    }
  };

  const handleProceedToGuestCheckout = () => {
    handleProceedToCheckout({ isGuestCheckout: true });
  };

  const handleProceedToCheckout = ({ isGuestCheckout = false } = {}) => {
    try {
      if (providerGroups.length === 0) return;

      if (!isGuestCheckout && !currentUser?.id) {
        history.push('/signup', { from: '/cart' });
        return;
      }

      // Organize items by provider - each provider group becomes one checkout session
      const checkoutGroups = providerGroups.map(group => {
        const items = group.items.map(item => {
          const listingId = item.listingId?.uuid || item.listingId;
          const listing = ensureListing(listingsById[listingId]);
          
          if (!listing || !listing.id) {
            return null;
          }

          const quantity = Number.parseInt(item.quantity, 10);
          const quantityMaybe = Number.isInteger(quantity) && quantity > 0 ? { quantity } : {};
          const deliveryMethodMaybe = item.deliveryMethod ? { deliveryMethod: item.deliveryMethod } : {};
          const priceVariantNameMaybe = item.priceVariantName ? { priceVariantName: item.priceVariantName } : {};
          // Use provider-level shippingMethodId
          const itemShippingMethodId = resolveShippingMethodIdForItem(group, item);
          const shippingMethodMaybe = itemShippingMethodId ? { shippingMethodId: itemShippingMethodId } : {};

          return {
            listing,
            listingId: listing.id.uuid, // Store as string UUID, not object
            orderData: {
              ...quantityMaybe,
              ...deliveryMethodMaybe,
              ...priceVariantNameMaybe,
              ...shippingMethodMaybe,
            },
          };
        }).filter(item => item !== null);

        const providerFrancoInfo = providerFrancoData.find(
          pf => pf.providerId === group.providerId
        );
        const shippingPriceAmount = calculateProviderShippingCost(group, providerFrancoInfo);
        const firstListingWithCurrency = group.listings.find(
          listing => listing?.attributes?.price?.currency
        );
        const shippingCurrencyValue =
          firstListingWithCurrency?.attributes?.price?.currency || currency;

        return {
          providerId: group.providerId,
          items,
          // Flag to indicate if this is a multi-item purchase
          isMultiItem: items.length > 1,
          shippingPrice: shippingPriceAmount,
          shippingCurrency: shippingCurrencyValue,
        };
      }).filter(group => group.items.length > 0);

      if (checkoutGroups.length === 0) {
        return;
      }



      // If multiple vendors, show modal first
      if (checkoutGroups.length > 1) {
        setPendingCheckoutGroups(checkoutGroups);
        setPendingGuestCheckout(isGuestCheckout);
        setIsMultiVendorModalOpen(true);
      } else {
        // Single vendor: proceed directly
        proceedToCheckout(checkoutGroups, { isGuestCheckout });
      }
    } catch (e) {
      // Silently handle error
    }
  };

  const handleMultiVendorProceed = () => {
    setIsMultiVendorModalOpen(false);
    if (pendingCheckoutGroups) {
      proceedToCheckout(pendingCheckoutGroups, { isGuestCheckout: pendingGuestCheckout });
      setPendingCheckoutGroups(null);
      setPendingGuestCheckout(false);
    }
  };

  const handleMultiVendorCancel = () => {
    setIsMultiVendorModalOpen(false);
    setPendingCheckoutGroups(null);
    setPendingGuestCheckout(false);
  };


  return (
    <Page title="Votre panier | Neokasa" robots="noindex, nofollow">
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <div className={css.header}>
            <H1 className={css.heading}>
            <FormattedMessage id="CartPage.title" />
            </H1>
          </div>

          {isInitialLoading ? (
            <div className={css.loadingContainer}>
              <IconSpinner className={css.spinner} />
              <p className={css.loadingText}>
                <FormattedMessage id="CartPage.loading" />
              </p>
            </div>
          ) : providerGroups.length === 0 ? (
            <div className={css.emptyCart}>
              <div className={css.emptyContent}>
                <H3 className={css.emptyTitle}>
                  <FormattedMessage id="CartPage.empty" />
                </H3>
                <p className={css.emptyDescription}>
                  <FormattedMessage id="CartPage.emptyDescription" />
                </p>
                <PrimaryButton className={css.continueShoppingBtn}>
                  <NamedLink name="SearchPage" className={css.continueShopping}>
                    <FormattedMessage id="CartPage.startShopping" />
                  </NamedLink>
                </PrimaryButton>
              </div>
              {shouldShowRecentRow ? (
                <div className={css.recentlyViewedSection}>
                  <H4 className={css.recentlyViewedTitle}>Vos derniers produits consultés</H4>
                  <div className={css.recentlyViewedGrid}>
                    {recentListingCardsToRender.map(listing => (
                      <ListingCard
                        key={listing.id.uuid}
                        listing={listing}
                        className={css.recentlyViewedCard}
                        renderSizes="(max-width: 767px) 44vw, 220px"
                      />
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            <div className={css.cartContent}>
              <div className={css.cartItems}>
                {providerGroups.map(group => {
                  // Get the first listing to determine the seller name
                  const firstListing = group.listings.find(listing => listing?.id?.uuid);
                  const sellerName = firstListing?.author?.attributes?.profile?.displayName || 
                                   firstListing?.author?.attributes?.profile?.firstName || 
                                   'Marketplace Seller';
                  
                  // Get franco data for this provider
                  const francoData = providerFrancoData.find(pf => pf.providerId === group.providerId);
                  const hasFreeShipping = francoData?.hasFreeShipping || false;
                  
                  return (
                    <div key={group.providerId} className={css.providerGroup}>
                      <H3 className={css.providerHeader}>
                        <FormattedMessage id="CartPage.soldBy" />
                        <span className={css.providerName}>
                          {sellerName}
                        </span>
                      </H3>

                    <div className={css.providerItems}>
                      {group.items.map((item, idx) => {
                        const listing = ensureListing(listingsById[item.listingId?.uuid || item.listingId]);

                        // Skip rendering if listing is not available or invalid
                        if (!listing || !listing.id) {
                          return null;
                        }

                        return (
                          <CartItem
                            key={`${item.listingId?.uuid || item.listingId}_${idx}`}
                            item={item}
                            listing={listing}
                            providerId={group.providerId}
                            onUpdateQuantity={updateCartItemQuantity}
                            onRemove={removeFromCart}
                            config={config}
                            hasFreeShipping={hasFreeShipping}
                          />
                        );
                      })}
                    </div>

                    <ProviderShippingSelectors
                      group={group}
                      providerSubtotal={
                        providerFrancoData.find(pf => pf.providerId === group.providerId)
                          ?.providerSubtotal || 0
                      }
                      currency={config.currency}
                      onUpdateShippingMethod={updateProviderShippingMethod}
                      idPrefix="cart-page-shipping"
                    />
                  </div>
                  );
                })}
              </div>

              <div className={css.cartSummary}>
                <div className={css.summaryCard}>
                  <H3 className={css.summaryTitle}>
                    <FormattedMessage id="CartPage.orderSummary" />
                  </H3>

                  {totalItems > 0 && (
                    <div className={css.itemCount}>
                      <FormattedMessage
                        id="CartPage.itemCount"
                        values={{ count: totalItems }}
                      />
                    </div>
                  )}

                  <div className={css.summaryRow}>
                    <span><FormattedMessage id="CartPage.subtotal" /></span>
                    <span>
                      {(() => {
                        const amount = estimated.subtotal || grandTotal;
                        if (!(amount > 0) || !isValidCurrency) return '—';
                        try { return formatMoney(intl, new Money(amount, currency)); } catch { return '—'; }
                      })()}
                    </span>
                  </div>

                  <div className={css.summaryRow}>
                    <span><FormattedMessage id="CartPage.shipping" /></span>
                    <span>
                      {(() => {
                        const shippingAmount = estimated.shipping || 0;
                        if (shippingAmount <= 0) {
                          return (
                            <span className={css.freeShipping}>
                              <FormattedMessage id="CartPage.freeShipping" defaultMessage="Gratuit" />
                            </span>
                          );
                        }
                        if (!isValidCurrency) return '—';
                        try {
                          return formatMoney(intl, new Money(shippingAmount, currency));
                        } catch {
                          return '—';
                        }
                      })()}
                    </span>
                  </div>

                  {(estimated.discount || 0) < 0 &&
                    (() => {
                      const lines = estimated.lines || [];
                      const bundleDiscountLines = lines.filter(
                        li =>
                          typeof li.code === 'string' &&
                          li.code.startsWith('line-item/bundle-discount-')
                      );
                      const hardcodedDiscountLines = lines.filter(
                        li =>
                          li.code === LINE_ITEM_PRINTEMPS_ROBOUP_DISCOUNT ||
                          li.code === LINE_ITEM_REOLINK_WEEKEND_2026_DISCOUNT ||
                          li.code === LINE_ITEM_T600MAI50_DISCOUNT
                      );
                      const hardcodedAmount = hardcodedDiscountLines.reduce(
                        (acc, li) => acc + (li?.lineTotal?.amount || 0),
                        0
                      );

                      const fmtDiscount = amt => {
                        if (!isValidCurrency) return '—';
                        try {
                          return formatMoney(intl, new Money(amt, currency));
                        } catch {
                          return '—';
                        }
                      };

                      return (
                        <>
                          {bundleDiscountLines.map(li => {
                            const packName =
                              (li.metadata && String(li.metadata.bundleTitle || '').trim()) ||
                              intl.formatMessage({
                                id: 'CartPage.bundleDiscountFallback',
                                defaultMessage: 'Pack',
                              });
                            return (
                              <div key={li.code} className={css.summaryRow}>
                                <span>
                                  <FormattedMessage
                                    id="CartPage.bundleDiscountRow"
                                    defaultMessage="Réduction {packName}"
                                    values={{ packName }}
                                  />
                                </span>
                                <span className={css.discountValue}>
                                  {fmtDiscount(li?.lineTotal?.amount || 0)}
                                </span>
                              </div>
                            );
                          })}
                          {hardcodedAmount < 0 ? (
                            <div className={css.summaryRow}>
                              <span>
                                <FormattedMessage id="CartPage.promoDiscount" />
                              </span>
                              <span className={css.discountValue}>
                                {fmtDiscount(hardcodedAmount)}
                              </span>
                            </div>
                          ) : null}
                          {bundleDiscountLines.length === 0 && hardcodedAmount >= 0 ? (
                            <div className={css.summaryRow}>
                              <span>
                                <FormattedMessage id="CartPage.promoDiscount" />
                              </span>
                              <span className={css.discountValue}>
                                {fmtDiscount(estimated.discount)}
                              </span>
                            </div>
                          ) : null}
                        </>
                      );
                    })()}

                  <div className={css.promoSection}>
                    <label className={css.promoLabel} htmlFor="cart-promo-code">
                      <FormattedMessage id="CartPage.promoCodeLabel" />
                    </label>
                    <div className={css.promoRow}>
                      <input
                        id="cart-promo-code"
                        className={css.promoInput}
                        type="text"
                        value={promoDraft}
                        onChange={e => {
                          setPromoDraft(e.target.value);
                          if (promoError) setPromoError(null);
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter' && promoDraft.trim()) {
                            onAddCartPromoCode(promoDraft);
                            setPromoDraft('');
                          }
                        }}
                        autoComplete="off"
                      />
                      <button
                        type="button"
                        className={css.promoApplyBtn}
                        onClick={() => {
                          if (!promoDraft.trim()) return;
                          onAddCartPromoCode(promoDraft);
                          setPromoDraft('');
                        }}
                      >
                        <FormattedMessage id="CartPage.promoApply" />
                      </button>
                    </div>
                    {promoError ? (
                      <p className={css.promoError}>{promoError}</p>
                    ) : null}
                    {cartPromoCodes && cartPromoCodes.length > 0 ? (
                      <div className={css.promoChipsRow}>
                        {(() => {
                          const activeBundleCodesSet = new Set(
                            (estimated.lines || [])
                              .filter(
                                li =>
                                  typeof li.code === 'string' &&
                                  li.code.startsWith('line-item/bundle-discount-')
                              )
                              .map(li => (li.metadata?.promoCode || '').toUpperCase())
                              .filter(Boolean)
                          );
                          const allBundleCodesSet = new Set([
                            ...activeBundleCodesSet,
                            ...(cartBundlePromoCodes || []).map(c => c.toUpperCase()),
                          ]);
                          return cartPromoCodes
                            .filter(code => !allBundleCodesSet.has(code.toUpperCase()))
                            .map(code => {
                          const isInvalid = invalidPromoCodes
                            .map(c => c.toUpperCase())
                            .includes(code.toUpperCase());
                          return (
                            <span
                              key={code}
                              className={classNames(css.promoChip, {
                                [css.promoChipInvalid]: isInvalid,
                              })}
                              title={
                                isInvalid
                                  ? intl.formatMessage({
                                      id: 'CartPage.promoInvalid',
                                      defaultMessage: 'Code non applicable',
                                    })
                                  : undefined
                              }
                            >
                              <span className={css.promoChipCode}>{code}</span>
                              <button
                                type="button"
                                className={css.promoChipRemove}
                                onClick={() => onRemoveCartPromoCode(code)}
                                aria-label={intl.formatMessage({
                                  id: 'CartPage.promoRemove',
                                  defaultMessage: 'Retirer le code promo',
                                })}
                              >
                                ✕
                              </button>
                            </span>
                          );
                        });
                        })()}
                      </div>
                    ) : null}
                  </div>

                  <div className={css.summaryDivider} />

                  <div className={css.summaryTotal}>
                    <span><FormattedMessage id="CartPage.total" /></span>
                    <span>
                      {(() => {
                        const amount = (estimated.total && estimated.total > 0) ? estimated.total : grandTotal;
                        if (!(amount > 0) || !isValidCurrency) return '—';
                        try { return formatMoney(intl, new Money(amount, currency)); } catch { return '—'; }
                      })()}
                    </span>
                  </div>

                  {/* Loyalty points earned (logged-in only) */}
                  {currentUser?.id && (() => {
                    const payAmount = (estimated.total && estimated.total > 0) ? estimated.total : grandTotal;
                    const points = payAmount > 0 ? Math.floor(payAmount / 100) : 0;
                    if (points <= 0) return null;
                    const remaining = Math.max(0, 500 - points);
                    return (
                      <div className={css.loyaltySummary}>
                        <span className={css.loyaltySummaryIcon}>🪙</span>
                        <span className={css.loyaltySummaryText}>
                          {points >= 500 ? (
                            <>Vous cumulez <strong>{points} points</strong> fidélité et bénéficiez déjà d'un avantage exclusif</>
                          ) : (
                            <>Vous cumulez <strong>{points} points</strong> fidélité. Plus que <strong>{remaining} points</strong> pour un avantage exclusif</>
                          )}
                        </span>
                      </div>
                    );
                  })()}

                  <div className={css.checkoutActions}>
                    <PrimaryButton className={css.checkoutBtn} onClick={() => handleProceedToCheckout()} disabled={providerGroups.length === 0}>
                      {currentUser?.id ? (
                        <FormattedMessage id="CartPage.proceedToCheckout" />
                      ) : (
                        <FormattedMessage id="CartPage.loginToProceed" defaultMessage="Se connecter pour payer" />
                      )}
                    </PrimaryButton>

                    {!currentUser?.id ? (
                      <button
                        type="button"
                        className={css.guestCheckoutBtn}
                        onClick={handleProceedToGuestCheckout}
                        disabled={providerGroups.length === 0}
                      >
                        <FormattedMessage
                          id="CartPage.proceedAsGuest"
                          defaultMessage="Payer sans compte (sans fidélité)"
                        />
                      </button>
                    ) : null}

                    <div className={css.paymentTrust}>
                      <div className={css.stripeSecurityRow}>
                        <FormattedMessage id="CartPage.securedByStripe" defaultMessage="Protected by Stripe" />
                        <StripeLogo />
                      </div>
                      <div className={css.acceptedCardsRow}>
                        <FormattedMessage id="CartPage.acceptedCards" defaultMessage="Accepted cards" />
                      </div>
                      <div className={css.cardsLogos}>
                        <IconCard brand="visa" className={css.cardLogo} />
                        <IconCard brand="mastercard" className={css.cardLogo} />
                        <IconCard brand="amex" className={css.cardLogo} />
                        <span className={css.cbBadge}>CB</span>
                      </div>
                    </div>

                    <NamedLink name="SearchPage" className={css.continueShopping}>
                      <FormattedMessage id="CartPage.continueShopping" />
                    </NamedLink>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className={css.cartSocialProof}>
            <SkeepersCarouselReviews title="Nos clients parlent de nous" />
            <TrustBanner />
          </div>
        </div>
      </LayoutSingleColumn>

      <MultiVendorCheckoutModal
        isOpen={isMultiVendorModalOpen}
        onClose={handleMultiVendorCancel}
        onProceed={handleMultiVendorProceed}
        vendorCount={pendingCheckoutGroups?.length || 0}
      />


      <PrintempsRoboupCartModal
        cart={cart}
        listingsById={listingsById}
        cartPromoCode={cartPromoCode}
        currentUser={currentUser}
        onApplyPromo={code => {
          setPromoDraft('');
          onAddCartPromoCode(code);
        }}
      />
    </Page>
  );
};

const mapStateToProps = state => {
  const { cart = {} } = state.Cart || {};
  const { currentUser } = state.user || {};
  // Collect listing refs from cart (handle both old and new format)
  const refs = Object.values(cart)
    .flatMap(providerData => {
      const items = Array.isArray(providerData) ? providerData : (providerData.items || []);
      return items;
    })
    .map(it => ({ id: { uuid: it.listingId }, type: 'listing' }));
  // Use marketplaceData selector to denormalize
  const { getMarketplaceEntities } = require('../../ducks/marketplaceData.duck');
  const listings = getMarketplaceEntities(state, refs) || [];
  const listingsById = listings.reduce((m, l) => ({ ...m, [l?.id?.uuid]: l }), {});
  return {
    cart,
    cartPromoCode: state.Cart?.cartPromoCode ?? null,
    cartPromoCodes: state.Cart?.cartPromoCodes ?? [],
    cartBundlePromoCodes: state.Cart?.cartBundlePromoCodes ?? [],
    listingsById,
    currentUser,
  };
};

const mapDispatchToProps = dispatch => ({
  loadCart: () => dispatch(loadCart()),
  updateCartItemQuantity: (params) => dispatch(updateCartItemQuantity(params)),
  removeFromCart: (params) => dispatch(removeFromCart(params)),
  updateProviderShippingMethod: (params) => dispatch(updateProviderShippingMethod(params)),
  onSetCartPromoCode: code => dispatch(setCartPromoCode(code)),
  onAddCartPromoCode: code => dispatch(addCartPromoCode(code)),
  onRemoveCartPromoCode: code => dispatch(removeCartPromoCode(code)),
  onSetCartPromoCodes: codes => dispatch(setCartPromoCodes(codes)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  clearCart: () => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem('demo-cart');
        localStorage.removeItem('demo-cart-promo');
        localStorage.removeItem('demo-cart-promos');
      } catch (e) {
        // Silently handle localStorage error
      }
    }
    dispatch(cartLoadSuccess({ cart: {}, cartPromoCode: null, cartPromoCodes: [] }));
  },
  fetchMissingListings: refs =>
    dispatch((innerDispatch, getState, sdk) => {
      const state = getState();
      const existing = require('../../ducks/marketplaceData.duck').getMarketplaceEntities(
        state,
        refs
      );
      const existingIds = new Set((existing || []).map(l => l?.id?.uuid));
      const toFetch = refs
        .map(r => r?.id?.uuid)
        .filter(uuid => uuid && !existingIds.has(uuid));
      
      // Get configuration for proper image variants
      const config = require('../../config/configDefault').default;
      const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } = config.layout.listingImage;
      const aspectRatio = aspectHeight / aspectWidth;
      
      const promises = Array.from(new Set(toFetch)).map(uuid =>
        sdk.listings.show({
          id: uuid,
          include: ['author', 'images'],
          'fields.image': [
            'variants.scaled-small',
            'variants.scaled-medium',
            `variants.${variantPrefix}`,
            `variants.${variantPrefix}-2x`,
          ],
          ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
          ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
        })
      );
      // allSettled (not all): one stale/deleted cart listing (a 404 from
      // sdk.listings.show) must not drop the listings that DID resolve.
      return Promise.allSettled(promises).then(results => {
        results.forEach(r => {
          if (r.status === 'fulfilled' && r.value) {
            innerDispatch(addMarketplaceEntities(r.value));
          }
        });
      });
    }),
});

const CartPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(CartPageComponent);

export default CartPage;


